import type {
  SipAccount,
  SipAccountDto,
  UpdateSipAccountsRequest,
} from '../../models/RoboCall/SipAccount';

export type SipCustomHeaderRow = {
  readonly key?: string;
  readonly value?: string;
};

export type SipAccountFormValues = Pick<
  SipAccountDto,
  | 'name'
  | 'username'
  | 'password'
  | 'server'
  | 'prefix'
  | 'registrarHost'
  | 'outboundProxy'
  | 'registerEnabled'
  | 'expiry'
  | 'registrationTimeout'
  | 'failureRetryInterval'
  | 'displayName'
  | 'accountAor'
  | 'contactUri'
  | 'sendUsernameInContact'
  | 'allowHeader'
  | 'exitOnUnequivocalFailure'
> & {
  default: boolean;
  maxRegisterAttempts?: number | null;
  customHeaderRows?: SipCustomHeaderRow[];
};

const DEFAULT_REGISTRATION_TIME = '00:00:00';
const DEFAULT_MAX_REGISTER_ATTEMPTS = 1;

const getAccountValue = (
  ...values: Array<string | null | undefined>
): string | undefined =>
  values.find((value): value is string => Boolean(value?.trim()));

export const getAccountTitle = (account: SipAccount): string =>
  getAccountValue(
    account.displayName,
    account.name,
    account.accountAor,
    account.server,
  ) ?? 'SIP-аккаунт';

export const getAccountAddress = (account: SipAccount): string =>
  getAccountValue(account.accountAor, account.contactUri, account.server) ??
  'Адрес не указан';

const parseCustomHeader = (customHeader: string): SipCustomHeaderRow => {
  const separatorIndex = customHeader.indexOf(':');

  if (separatorIndex < 0) {
    return { key: customHeader.trim(), value: '' };
  }

  return {
    key: customHeader.slice(0, separatorIndex).trim(),
    value: customHeader.slice(separatorIndex + 1).trim(),
  };
};

const serializeCustomHeaderRows = (
  customHeaderRows: SipCustomHeaderRow[] | undefined,
): string[] | null => {
  const headers = (customHeaderRows ?? []).reduce<string[]>((headers, row) => {
    const headerKey = row.key?.trim() ?? '';
    const headerValue = row.value?.trim() ?? '';

    if (!headerKey && !headerValue) return headers;
    headers.push(
      headerKey && headerValue
        ? `${headerKey}: ${headerValue}`
        : headerKey || headerValue,
    );
    return headers;
  }, []);

  return headers.length > 0 ? headers : null;
};

const isLockedDefaultAccount = (
  isCreateMode: boolean,
  accountCount: number,
): boolean => (isCreateMode ? accountCount === 0 : accountCount <= 1);

export const getInitialFormValues = (
  account: SipAccount | undefined,
  isCreateMode: boolean,
  accountCount: number,
): SipAccountFormValues => ({
  name: account?.name ?? '',
  username: account?.username ?? '',
  password: account?.password ?? '',
  server: account?.server ?? '',
  prefix: account?.prefix ?? '',
  default:
    Boolean(account?.default) ||
    isLockedDefaultAccount(isCreateMode, accountCount),
  registrarHost: account?.registrarHost ?? '',
  outboundProxy: account?.outboundProxy ?? '',
  registerEnabled: account?.registerEnabled ?? isCreateMode,
  expiry: account?.expiry ?? DEFAULT_REGISTRATION_TIME,
  registrationTimeout:
    account?.registrationTimeout ?? DEFAULT_REGISTRATION_TIME,
  failureRetryInterval:
    account?.failureRetryInterval ?? DEFAULT_REGISTRATION_TIME,
  maxRegisterAttempts:
    account?.maxRegisterAttempts ??
    (isCreateMode ? DEFAULT_MAX_REGISTER_ATTEMPTS : null),
  displayName: account?.displayName ?? '',
  accountAor: account?.accountAor ?? '',
  contactUri: account?.contactUri ?? '',
  sendUsernameInContact: account?.sendUsernameInContact ?? false,
  exitOnUnequivocalFailure: account?.exitOnUnequivocalFailure ?? isCreateMode,
  allowHeader: account?.allowHeader ?? '',
  customHeaderRows: account?.customHeaders?.map(parseCustomHeader) ?? [],
});

const normalizeDefaultAccounts = (
  accounts: SipAccountDto[],
  currentAccount: SipAccountDto,
): SipAccountDto[] => {
  const currentAccountIndex = accounts.findIndex(
    (item) =>
      item === currentAccount ||
      (item.id !== undefined && item.id === currentAccount.id),
  );
  const existingDefaultIndex = accounts.findIndex((item) => item.default);

  if (!currentAccount.default && existingDefaultIndex < 0) return accounts;

  const defaultIndex =
    currentAccount.default && currentAccountIndex >= 0
      ? currentAccountIndex
      : existingDefaultIndex >= 0
        ? existingDefaultIndex
        : Math.max(currentAccountIndex, 0);

  return accounts.map((item, index) => ({
    ...item,
    default: index === defaultIndex,
  }));
};

export const buildSipAccountSaveRequest = (
  values: SipAccountFormValues,
  baseAccounts: SipAccount[],
  account: SipAccount | undefined,
  isCreateMode: boolean,
): UpdateSipAccountsRequest => {
  if (!isCreateMode) {
    return {
      sipAccounts: baseAccounts.map((item) =>
        item.id === account?.id ? { ...item, name: values.name } : item,
      ),
    };
  }

  const { customHeaderRows, maxRegisterAttempts, ...restValues } = values;
  const currentAccount: SipAccount = {
    ...(account ?? {}),
    id: account?.id ?? crypto.randomUUID(),
    ...restValues,
    customHeaders: serializeCustomHeaderRows(customHeaderRows),
    maxRegisterAttempts:
      maxRegisterAttempts === undefined || maxRegisterAttempts === null
        ? undefined
        : maxRegisterAttempts,
    default:
      isLockedDefaultAccount(isCreateMode, baseAccounts.length) ||
      Boolean(account?.default) ||
      Boolean(values.default),
  };
  return {
    sipAccounts: normalizeDefaultAccounts(
      [...baseAccounts, currentAccount],
      currentAccount,
    ),
  };
};
